// TanStack Query over the FellowshipSource. Screens call these hooks; they never call the source.
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import type { FellowshipSource, Page } from "./source";
import type { NewPost, Post, ProfileDetailsInput, Reaction } from "./types";

export const SourceContext = createContext<FellowshipSource | null>(null);

export function useSource(): FellowshipSource {
  const s = useContext(SourceContext);
  if (!s) throw new Error("SourceContext missing");
  return s;
}

export const keys = {
  config: ["config"] as const,
  session: ["session"] as const,
  votd: ["votd"] as const,
  feed: ["feed"] as const,
  post: (id: string) => ["post", id] as const,
  comments: (id: string) => ["comments", id] as const,
  saved: ["saved"] as const,
  profile: (h: string) => ["profile", h] as const,
  profilePosts: (h: string) => ["profilePosts", h] as const,
  friends: ["friends"] as const,
  people: (q: string) => ["people", q] as const,
  notifications: ["notifications"] as const,
  passage: (r: string) => ["passage", r] as const,
  passagePosts: (r: string) => ["passagePosts", r] as const,
  unread: ["unread"] as const,
  newCount: (since: string) => ["newCount", since] as const,
  blocks: ["blocks"] as const,
};

type FeedData = InfiniteData<Page<Post>, string | undefined>;

export const useConfig = () => {
  const s = useSource();
  return useQuery({ queryKey: keys.config, queryFn: () => s.config(), staleTime: 120_000 });
};
/** Web purchases (Stripe) are open: the API's `web.purchases` switch. */
export const usePurchasesOpen = () => useConfig().data?.features?.["web.purchases"]?.state === "on";

/** After signing in, onboarding or signing out: fetch the session fresh, so route guards see it at once. */
export const refreshSession = (qc: QueryClient, source: FellowshipSource) =>
  qc.fetchQuery({ queryKey: keys.session, queryFn: () => source.session(), staleTime: 0 });

/** Which of Google and Apple this server offers (asked once per page load). */
export const useAuthProviders = () => {
  const s = useSource();
  return useQuery({ queryKey: ["auth", "providers"], queryFn: () => s.authProviders(), staleTime: Infinity, retry: false });
};

export const useSession = () => {
  const s = useSource();
  return useQuery({ queryKey: keys.session, queryFn: () => s.session() });
};

/** FS-136: the tradition list, defaults, limits and the consent sentence for the profile's details. */
export const useProfileOptions = () => {
  const s = useSource();
  return useQuery({ queryKey: ["profile-options"], queryFn: () => s.profileOptions(), staleTime: 3_600_000 });
};

/**
 * FS-136: save your location or faith details, or who sees them. Your own values are on
 * `useSession().data.profile`; a friend's, as you may see them, on `useProfile(handle).data.details`.
 * Setting a faith field the first time needs `faithConsent: true` (after the reader agreed to the
 * options' faithConsentText), else it fails with a message to show.
 */
export const useUpdateProfileDetails = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProfileDetailsInput) => s.updateProfileDetails(input),
    onSuccess: (profile) => {
      qc.setQueryData(keys.session, (old: Awaited<ReturnType<FellowshipSource["session"]>> | undefined) => (old ? { ...old, profile } : old));
      void qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });
};
export const useVotd = () => {
  const s = useSource();
  return useQuery({ queryKey: keys.votd, queryFn: () => s.votd() });
};
/**
 * The feed, a page at a time (the API's cursor), read as one list: data is { items, nextCursor } over
 * every page loaded; fetchNextPage loads earlier posts. New posts are counted, not pushed in
 * (useNewCount), so the page never moves under the reader.
 */
export const useFeed = () => {
  const s = useSource();
  return useInfiniteQuery({
    queryKey: keys.feed,
    queryFn: ({ pageParam }) => s.feed(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    select: (d: FeedData) => ({ items: d.pages.flatMap((p) => p.items), nextCursor: d.pages.at(-1)?.nextCursor ?? null }),
  });
};
/** Friends' posts since the newest one shown, checked each minute while the tab is visible (app flow §3). */
export const useNewCount = (since: string | null) => {
  const s = useSource();
  return useQuery({
    queryKey: keys.newCount(since ?? ""),
    queryFn: () => s.newCount(since!),
    enabled: !!since,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
};
export const useBlocks = () => {
  const s = useSource();
  return useQuery({ queryKey: keys.blocks, queryFn: () => s.blocks() });
};
export const usePost = (id: string) => {
  const s = useSource();
  return useQuery({ queryKey: keys.post(id), queryFn: () => s.post(id) });
};
export const useComments = (id: string) => {
  const s = useSource();
  return useQuery({ queryKey: keys.comments(id), queryFn: () => s.comments(id) });
};
export const useSaved = () => {
  const s = useSource();
  return useQuery({ queryKey: keys.saved, queryFn: () => s.saved() });
};
export const useProfile = (handle: string) => {
  const s = useSource();
  return useQuery({ queryKey: keys.profile(handle), queryFn: () => s.profile(handle) });
};
export const useProfilePosts = (handle: string) => {
  const s = useSource();
  return useQuery({ queryKey: keys.profilePosts(handle), queryFn: () => s.profilePosts(handle) });
};
export const useFriends = () => {
  const s = useSource();
  return useQuery({ queryKey: keys.friends, queryFn: () => s.friends() });
};
export const usePeople = (q: string) => {
  const s = useSource();
  return useQuery({ queryKey: keys.people(q), queryFn: () => s.findPeople(q), enabled: q.trim().length > 0 });
};
export const useNotifications = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useQuery({
    queryKey: keys.notifications,
    queryFn: async () => {
      const items = await s.notifications();
      void qc.invalidateQueries({ queryKey: keys.unread });
      return items;
    },
  });
};
export const usePassagePosts = (ref: string) => {
  const s = useSource();
  return useQuery({ queryKey: keys.passagePosts(ref), queryFn: () => s.passagePosts(ref) });
};
/** The one number on the navigation (PRD N3): unread activity. Checked each minute while visible. */
export const useUnreadCount = () => {
  const s = useSource();
  return useQuery({ queryKey: keys.unread, queryFn: () => s.unreadCount(), refetchInterval: 60_000 });
};
/** A passage in the WEB. `maxVerses` for a quote (the composer: five); none for a passage page. */
export const usePassage = (ref: string, maxVerses?: number) => {
  const s = useSource();
  return useQuery({
    queryKey: [...keys.passage(ref), maxVerses ?? 0],
    queryFn: () => s.lookupPassage(ref, maxVerses),
    enabled: ref.trim().length > 2,
    staleTime: Infinity,
  });
};

const mapFeed = (old: FeedData | undefined, f: (p: Post) => Post): FeedData | undefined =>
  old ? { ...old, pages: old.pages.map((pg) => ({ ...pg, items: pg.items.map(f) })) } : old;

/** Put a changed post everywhere it is cached: its own page, the feed, saved, and profiles. */
function patchPost(qc: QueryClient, next: Post) {
  qc.setQueryData(keys.post(next.id), next);
  qc.setQueryData(keys.feed, (old: FeedData | undefined) => mapFeed(old, (p) => (p.id === next.id ? next : p)));
  qc.setQueriesData({ queryKey: ["profilePosts"] }, (old: Post[] | undefined) => old?.map((p) => (p.id === next.id ? next : p)));
  qc.invalidateQueries({ queryKey: keys.saved });
}

/**
 * Optimistic updates (as X and Instagram do): the change shows at once in every list that holds the
 * post, and every one of them is restored exactly if the server refuses.
 */
const POST_LISTS = [["feed"], ["post"], ["profilePosts"], ["saved"], ["passagePosts"]] as const;

async function optimistic(qc: QueryClient, postId: string, change: (p: Post) => Post) {
  await Promise.all(POST_LISTS.map((k) => qc.cancelQueries({ queryKey: k })));
  const snapshot = POST_LISTS.flatMap((k) => qc.getQueriesData({ queryKey: k }));
  const apply = (p: Post) => (p.id === postId ? change(p) : p);
  qc.setQueriesData({ queryKey: ["feed"] }, (old: FeedData | undefined) => mapFeed(old, apply));
  qc.setQueriesData({ queryKey: ["post", postId] }, (old: Post | null | undefined) => (old ? apply(old) : old));
  for (const k of [["profilePosts"], ["saved"], ["passagePosts"]] as const)
    qc.setQueriesData({ queryKey: k }, (old: Post[] | undefined) => old?.map(apply));
  return { snapshot };
}

function rollback(qc: QueryClient, ctx: { snapshot: [readonly unknown[], unknown][] } | undefined) {
  ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key, data));
}

export const useReact = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, reaction }: { postId: string; reaction: Reaction | null }) => s.react(postId, reaction),
    onMutate: ({ postId, reaction }) =>
      optimistic(qc, postId, (p) => {
        const reactions = { ...p.reactions };
        if (p.myReaction) reactions[p.myReaction] -= 1;
        if (reaction) reactions[reaction] += 1;
        return { ...p, reactions, myReaction: reaction };
      }),
    onError: (_e, _v, ctx) => rollback(qc, ctx),
    onSuccess: (p) => patchPost(qc, p),
  });
};
export const useSave = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, saved }: { postId: string; saved: boolean }) => s.setSaved(postId, saved),
    onMutate: ({ postId, saved }) => optimistic(qc, postId, (p) => ({ ...p, saved })),
    onError: (_e, _v, ctx) => rollback(qc, ctx),
    onSuccess: (p) => patchPost(qc, p),
  });
};
export const useCreatePost = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewPost) => s.createPost(input),
    meta: { inline: true },
    onSuccess: (p) => {
      qc.setQueryData(keys.post(p.id), p);
      qc.invalidateQueries({ queryKey: keys.feed });
      qc.invalidateQueries({ queryKey: keys.votd });
      qc.invalidateQueries({ queryKey: ["profilePosts"] });
    },
  });
};
export const useEditPost = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body, title }: { id: string; body: string; title?: string }) => s.editPost(id, { body, title }),
    meta: { inline: true },
    onSuccess: (p) => patchPost(qc, p),
  });
};
export const useDeleteComment = (postId: string) => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => s.deleteComment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.comments(postId) });
      qc.invalidateQueries({ queryKey: keys.post(postId) });
      qc.invalidateQueries({ queryKey: keys.feed });
    },
  });
};
export const useShareVotd = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => s.shareVotd(), onSuccess: (v) => qc.setQueryData(keys.votd, v) });
};
export const useDeletePost = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => s.deletePost(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.feed });
      qc.invalidateQueries({ queryKey: ["profilePosts"] });
    },
  });
};
export const useAddComment = (postId: string) => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => s.addComment(postId, body),
    meta: { inline: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.comments(postId) });
      qc.invalidateQueries({ queryKey: keys.post(postId) });
      qc.invalidateQueries({ queryKey: keys.feed });
      qc.invalidateQueries({ queryKey: ["profilePosts"] });
      qc.invalidateQueries({ queryKey: ["comments"] });
      qc.invalidateQueries({ queryKey: keys.saved });
    },
  });
};
export const useUnblock = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => s.unblock(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.blocks });
      qc.invalidateQueries({ queryKey: ["people"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });
};
export const useLikeVotd = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({ mutationFn: (liked: boolean) => s.likeVotd(liked), onSuccess: (v) => qc.setQueryData(keys.votd, v) });
};
export const useFriendAction = () => {
  const s = useSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, userId, handle }: { action: "request" | "accept" | "decline" | "remove" | "block"; userId: string; handle?: string }) =>
      action === "remove"
        ? s.removeFriend(userId)
        : action === "request"
        ? s.requestFriend(userId, handle)
        : action === "accept"
          ? s.acceptFriend(userId)
          : action === "block"
            ? s.block(userId)
            : s.declineFriend(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.friends });
      qc.invalidateQueries({ queryKey: ["people"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: keys.feed });
      qc.invalidateQueries({ queryKey: ["profilePosts"] });
      qc.invalidateQueries({ queryKey: ["comments"] });
      qc.invalidateQueries({ queryKey: keys.saved });
      qc.invalidateQueries({ queryKey: keys.blocks });
      qc.invalidateQueries({ queryKey: keys.unread });
    },
  });
};
